import { connect } from 'react-redux';
import { getProductTaxonomy } from 'features/taxonomy/actions';

import React, { useEffect } from 'react';
import { withTheme } from '@material-ui/core/styles';
import styled, { ThemeProvider } from 'styled-components';
import _ from 'lodash';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';

import Typography from '@material-ui/core/Typography';

import CircleIcon from 'mdi-material-ui/Circle';

import FeaturedCard from '../elements/FeaturedCard';

const SnippetTypography = styled(Typography)`
  em {
    background: rgba(255, 255, 25, 0.25);
  }
`;

const ResultCard = styled(FeaturedCard)`
  width: 100%;
  display: block;
  text-decoration: none;
  transition: all 0.15s ease-in-out;
  margin-bottom: 24px;
  display: flex;
  > div {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  &:hover {
    box-shadow: 0 4px 25px 0 rgba(0, 0, 0, 0.15);
  }
`;

const MetaTypography = styled(Typography)`
  margin: 6px ${props => (props.padRight ? '80px' : 0)} 24px 0;
`;

const ResultFacets = styled.div`
  margin-top: auto;
  display: flex;
  flex-wrap: wrap;
  max-width: 100%;
`;

const ProductIcon = ({ products, product }) => {
  const url = _.get(_.find(products, ['name', product]), 'icon.url');
  if (!url) return null;
  return <img width="24" height="24" src={url} alt={product} />;
};

const ProductInfo = styled(({ products, productResult, ...other }) =>
  _.isArray(productResult) ? (
    productResult.map((product, idx) => (
      <Typography variant="subtitle2" key={idx} noWrap {...other}>
        <ProductIcon products={products} product={product} />
        {product}{' '}
      </Typography>
    ))
  ) : (
    <Typography variant="subtitle2" noWrap {...other}>
      <ProductIcon products={products} product={productResult} />
      {productResult}{' '}
    </Typography>
  )
)`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  margin: 10px 0 0 0;
  min-height: 24px;
  max-width: 100%;
  :not(:last-of-type) {
    margin-right: 16px;
  }
  img {
    width: 24px;
    height: 24px;
    margin-right: 4px;
  }
`;

const ResultInfo = styled(({ result, ...other }) => (
  <span {...other}>
    <CircleIcon />
    {_.isArray(result) ? (
      <Typography variant="subtitle2" noWrap>
        {result.join(', ')}
      </Typography>
    ) : (
      <Typography variant="subtitle2">{result}</Typography>
    )}
  </span>
))`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  margin: 10px 0 0 0;
  min-height: 24px;
  max-width: 100%;

  svg {
    fill: ${props => props.theme.palette.divider};
    width: 8px;
    height: 8px;
    margin: 0 10px;
  }
`;

const PublicationResultCard = ({
  id,
  url,
  featured_publication,
  journal,
  published_at,
  title,
  author,
  excerpt,
  body,
  product,
  application,
  research_area,
  loadProductTaxonomy,
  getProductTaxonomy,
  productTaxonomy,
  size,
  theme
}) => {
  useEffect(() => {
    if (
      productTaxonomy &&
      productTaxonomy.length === 0 &&
      loadProductTaxonomy
    ) {
      getProductTaxonomy();
    }
  }, [productTaxonomy, getProductTaxonomy, loadProductTaxonomy]);

  return (
    <ThemeProvider theme={theme}>
      <ResultCard
        key={_.get(id, 'raw')}
        component={Link}
        to={_.get(url, 'raw', '').replace('https://www.synthego.com', '')}
        featured={featured_publication}
      >
        <MetaTypography
          variant="overline"
          padRight={featured_publication}
          gutterBottom
        >
          {_.get(journal, 'raw')},{' '}
          {dayjs(_.get(published_at, 'raw')).format('MMM YYYY')}
        </MetaTypography>
        <Typography variant={size === 'small' ? 'h5' : 'h4'} gutterBottom>
          {_.get(title, 'raw')}
        </Typography>
        <Typography variant="body2" component="div" gutterBottom noWrap>
          {author && (
            <>
              {_.isArray(author.raw) ? `${author.raw[0]} et al.` : author.raw}
            </>
          )}
          &nbsp;
          <em
            dangerouslySetInnerHTML={{
              __html: excerpt ? excerpt.raw : ''
            }}
          />
          {}
        </Typography>
        {body && body.snippet && (
          <SnippetTypography variant="body2" gutterBottom>
            ...
            <span
              dangerouslySetInnerHTML={{
                __html: body.snippet
              }}
            />
            ...
          </SnippetTypography>
        )}
        <ResultFacets>
          {product && productTaxonomy && (
            <ProductInfo
              products={productTaxonomy}
              productResult={_.get(product, 'raw')}
            />
          )}
          {application && <ResultInfo result={_.get(application, 'raw')} />}
          {research_area && <ResultInfo result={_.get(research_area, 'raw')} />}
        </ResultFacets>
      </ResultCard>
    </ThemeProvider>
  );
};

export default withTheme()(
  connect(
    state => ({
      productTaxonomy: state.taxonomy.products
    }),
    {
      getProductTaxonomy
    }
  )(PublicationResultCard)
);
